import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import arrowIconSecondary from 'src/assets/svg/arrow-brand-secondary-500.svg';
import arrowIcon from 'src/assets/svg/arrow.svg';
import { Direction } from '../../types';
import { useScrollSnapCarousel } from './useScrollSnapCarousel';

interface IButtonProps {
  direction: 'left' | 'right';
  hasItemsToShow: boolean;
  onClick: () => void;
  numItemsSelectedOnLeft?: number;
  numItemsSelectedOnRight?: number;
  visibleOnSmallScreen?: boolean;
  visibleOnLargeScreen?: boolean;
  isLightBackground?: boolean;
}

const ScrollButton: React.FC<IButtonProps> = ({
  direction,
  hasItemsToShow,
  onClick,
  numItemsSelectedOnLeft,
  numItemsSelectedOnRight,
  visibleOnSmallScreen = false,
  visibleOnLargeScreen = false,
}) => {
  const hasSelected = direction === 'left' ? numItemsSelectedOnLeft : numItemsSelectedOnRight;

  const buttonClassName = cn(
    'absolute top-1/2 z-50 -translate-y-1/2',
    'flex items-center justify-center',
    'h-10 transition-all',
    'border-none',
    'shadow-[0px_0px_5px_rgba(0,0,0,0.1)]',
    {
      'left-2': direction === 'left',
      'right-2': direction === 'right',
      'bg-gray-600 w-[50px] rounded-[40px]': hasSelected,
      'w-10 rounded-full bg-[#f8f8f8]': !hasSelected,
      hidden: !hasItemsToShow,
      invisible: !visibleOnSmallScreen,
      'md:flex md:visible': visibleOnLargeScreen,
      'md:invisible': !visibleOnLargeScreen,
    }
  );

  return (
    <button onClick={onClick} aria-label={`Scroll ${direction}`} className={buttonClassName}>
      {direction === 'left' ? (
        <>
          <img src={hasSelected ? arrowIconSecondary : arrowIcon} alt='Left arrow' className='size-auto' />
          {hasSelected && (
            <span className='mx-2 font-sans text-xs font-semibold leading-4 text-white'>{numItemsSelectedOnLeft}</span>
          )}
        </>
      ) : (
        <>
          {hasSelected && (
            <span className='mx-2 font-sans text-xs font-semibold leading-4 text-white'>{numItemsSelectedOnRight}</span>
          )}
          <img src={hasSelected ? arrowIconSecondary : arrowIcon} alt='Right arrow' className='size-auto rotate-180' />
        </>
      )}
    </button>
  );
};

interface IProps {
  addLeftMargin?: boolean;
  showSelectedOffscreenCount?: boolean;
  onLeftScrollButtonClick?: () => void;
  onRightScrollButtonClick?: () => void;
  scrollSnapOnSmallScreen?: boolean;
  isLightBackground?: boolean;
  centerContent?: boolean;
  showSideGradients?: boolean;
  showScrollButtonsOnSmallScreen?: boolean;
  showScrollButtonsOnLargeScreen?: boolean;
  gap?: number;
  itemLayoutDirection?: Direction;
  wrapperClassName?: string;
}

export const ScrollSnapCarousel: React.FC<React.PropsWithChildren<IProps>> = ({
  addLeftMargin = false,
  children,
  showSelectedOffscreenCount,
  onLeftScrollButtonClick,
  onRightScrollButtonClick,
  wrapperClassName,
  scrollSnapOnSmallScreen = false,
  isLightBackground = false,
  centerContent = true,
  showSideGradients = true,
  showScrollButtonsOnSmallScreen = false,
  showScrollButtonsOnLargeScreen = false,
  gap = 16,
  itemLayoutDirection = Direction.Horizontal,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [carouselRef, setCarouselRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      setCarouselRef(ref.current);
    }
  }, []);
  const { hasItemsOnLeft, hasItemsOnRight, scrollLeft, scrollRight, numItemsSelectedOnLeft, numItemsSelectedOnRight } =
    useScrollSnapCarousel(carouselRef, scrollSnapOnSmallScreen);

  const handleLeftScrollButtonClick = () => {
    onLeftScrollButtonClick?.();
    scrollLeft();
  };

  const handleRightScrollButtonClick = () => {
    onRightScrollButtonClick?.();
    scrollRight();
  };

  // Ensure rest is an object, otherwise it will error since the iterable is null or undefined
  const safeRest = rest || {};

  return (
    <div
      className={cn('relative flex w-full items-center', {
        'justify-center': centerContent,
        'justify-start': !centerContent,
      })}
      {...safeRest}>
      <div
        ref={ref}
        className={cn(
          'flex pr-4',
          'overflow-x-auto overflow-y-clip',
          'snap-x snap-mandatory',
          '[&::-webkit-scrollbar]:hidden',
          'scrollbar-none',
          {
            '[&>div:first-child]:ml-4': addLeftMargin,
            '[&>div:first-child]:ml-0': !addLeftMargin,
            'sm:snap-none sm:snap-mandatory': !scrollSnapOnSmallScreen,
            'sm:snap-none': scrollSnapOnSmallScreen,
            'items-center justify-start ': itemLayoutDirection === Direction.Horizontal,
            'flex-col place-content-start': itemLayoutDirection === Direction.Vertical,
          },
          wrapperClassName
        )}
        style={{ gap: `${gap}px` }}>
        {children}
      </div>

      {showSideGradients && (
        <>
          <div
            className={cn('absolute left-0 z-40 h-full w-[35px]', 'transition-transform duration-100 ease-in-out', {
              visible: hasItemsOnLeft,
              invisible: !hasItemsOnLeft,
              'bg-gradient-to-r from-white to-transparent': isLightBackground,
              'bg-gradient-to-r from-[#f8f8f8] to-transparent': !isLightBackground,
            })}
          />
          <div
            className={cn('absolute right-0 z-40 h-full w-[35px]', 'transition-transform duration-100 ease-in-out', {
              visible: hasItemsOnRight,
              invisible: !hasItemsOnRight,
              'bg-gradient-to-l from-white to-transparent': isLightBackground,
              'bg-gradient-to-l from-[#f8f8f8] to-transparent': !isLightBackground,
            })}
          />
        </>
      )}

      <ScrollButton
        direction='left'
        hasItemsToShow={hasItemsOnLeft}
        onClick={handleLeftScrollButtonClick}
        numItemsSelectedOnLeft={showSelectedOffscreenCount ? numItemsSelectedOnLeft : undefined}
        visibleOnSmallScreen={showScrollButtonsOnSmallScreen}
        visibleOnLargeScreen={showScrollButtonsOnLargeScreen}
      />
      <ScrollButton
        direction='right'
        hasItemsToShow={hasItemsOnRight}
        onClick={handleRightScrollButtonClick}
        numItemsSelectedOnRight={showSelectedOffscreenCount ? numItemsSelectedOnRight : undefined}
        visibleOnSmallScreen={showScrollButtonsOnSmallScreen}
        visibleOnLargeScreen={showScrollButtonsOnLargeScreen}
      />
    </div>
  );
};
